import { getCosmicProductData } from './cosmic/fetchFunctions/getCosmicProductData'
import type { SupportedLocale } from '../utils/localization/getFlattenedLocales'
import type { CMSResult } from './types/CMSError'
import { logError } from '../utils/shared/log'

export type GetProductDataPropsType = {
  productSlug: string
  locale: SupportedLocale
}

export const getProductData = async ({
  productSlug,
  locale,
}: GetProductDataPropsType): Promise<
  CMSResult<ReturnType<typeof getCosmicProductData>>
> => {
  try {
    const cosmicProductData = await getCosmicProductData({
      productSlug,
      locale,
    })

    if (!cosmicProductData) {
      const error = {
        code: 'COSMIC_PRODUCT_NOT_FOUND',
        message:
          "Sorry, we couldn't find the product you're looking for. It may have been moved or removed.",
        source: 'cosmic' as const,
        timestamp: new Date().toISOString(),
      }

      // Enhanced logging for product not found
      logError('Product Not Found in CMS', {
        ...error,
        productSlug,
        locale,
        url: typeof window !== 'undefined' ? window.location.href : 'unknown',
        userAgent:
          typeof window !== 'undefined'
            ? window.navigator.userAgent
            : 'unknown',
        type: 'cms-product-not-found',
      })

      return { error }
    }

    return cosmicProductData
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'
    const errorDetails = {
      code: 'COSMIC_PRODUCT_FETCH_FAILED',
      message:
        "We're having trouble loading this product's information. Please try refreshing the page.",
      source: 'cosmic' as const,
      details: errorMessage,
      timestamp: new Date().toISOString(),
    }

    // Enhanced logging for fetch failures
    logError('Product Fetch Failed in CMS', {
      ...errorDetails,
      productSlug,
      locale,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent:
        typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      type: 'cms-product-fetch-failed',
    })

    return { error: errorDetails }
  }
}
